import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Lock, Check, X, Sliders, Info, ShieldAlert } from "lucide-react";

const CONSENT_STORAGE_KEY = "bugzx_dpdp_consent";

export default function CookieConsentBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: true,
    marketing: false,
  });

  useEffect(() => {
    try {
      const saved = localStorage.getItem(CONSENT_STORAGE_KEY);
      if (!saved) {
        // Delay appearance slightly for smooth UX
        const timer = setTimeout(() => setIsVisible(true), 1000);
        return () => clearTimeout(timer);
      } else {
        const parsed = JSON.parse(saved);
        setPreferences(parsed.preferences || { necessary: true, analytics: true, marketing: false });
      }
    } catch (e) {
      setIsVisible(true);
    }

    const handleOpen = () => {
      setShowPreferences(true);
      setIsVisible(true);
    };

    window.addEventListener("open-cookie-preferences", handleOpen);
    return () => window.removeEventListener("open-cookie-preferences", handleOpen);
  }, []);

  const saveConsent = (prefs) => {
    const payload = {
      consentGiven: true,
      timestamp: new Date().toISOString(),
      jurisdiction: "IN_DPDP_2023",
      preferences: prefs,
    };
    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {
      // ignore
    }
    setPreferences(prefs);
    setIsVisible(false);
    setShowPreferences(false);
  };

  const handleAcceptAll = () => {
    saveConsent({ necessary: true, analytics: true, marketing: true });
  };

  const handleRejectNonEssential = () => {
    saveConsent({ necessary: true, analytics: false, marketing: false });
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Banner */}
      <div className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:max-w-xl z-50 animate-in fade-in slide-in-from-bottom-5 duration-300">
        <div className="rounded-3xl border border-black/15 bg-white/95 backdrop-blur-md p-5 md:p-6 shadow-[0_20px_60px_rgba(0,0,0,0.18)] text-black">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-2xl bg-black text-white flex items-center justify-center shrink-0 mt-0.5">
              <ShieldCheck className="h-5 w-5 text-emerald-400" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-orbitron font-bold text-sm">Privacy &amp; DPDP Notice</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-black/5 font-semibold text-black/70">
                  INDIA DPDP 2023
                </span>
              </div>
              <p className="mt-2 text-xs text-black/70 leading-relaxed">
                We use cookies and process necessary digital personal data to deliver secure services, remember your preferences, and analyze platform performance in accordance with the <strong>Digital Personal Data Protection Act, 2023</strong>.
              </p>
              <div className="mt-2 text-[11px] text-black/60 flex items-center gap-3">
                <Link to="/privacy" className="underline underline-offset-2 font-medium hover:text-black">
                  Privacy Policy
                </Link>
                <span>•</span>
                <Link to="/terms" className="underline underline-offset-2 font-medium hover:text-black">
                  Terms of Service
                </Link>
              </div>
            </div>
          </div>

          <div className="mt-4 pt-3 border-t border-black/10 flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowPreferences(true)}
              className="px-3.5 py-2 rounded-full border border-black/15 text-xs font-semibold hover:bg-black/5 transition-colors flex items-center gap-1.5"
            >
              <Sliders className="h-3.5 w-3.5" /> Preferences
            </button>
            <button
              type="button"
              onClick={handleRejectNonEssential}
              className="px-3.5 py-2 rounded-full border border-black/15 text-xs font-semibold hover:bg-black/5 transition-colors"
            >
              Decline Optional
            </button>
            <button
              type="button"
              onClick={handleAcceptAll}
              className="px-4 py-2 rounded-full bg-black text-white text-xs font-bold tracking-wider hover:bg-black/85 transition-colors flex items-center gap-1.5"
            >
              <Check className="h-3.5 w-3.5" /> ACCEPT ALL
            </button>
          </div>
        </div>
      </div>

      {/* Preferences Modal */}
      {showPreferences && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl border border-black/15 max-w-lg w-full p-6 shadow-2xl space-y-5">
            <div className="flex items-center justify-between pb-3 border-b border-black/10">
              <div className="flex items-center gap-2.5">
                <div className="h-8 w-8 rounded-xl bg-black text-white flex items-center justify-center">
                  <Sliders className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="font-orbitron font-bold text-base">Cookie &amp; Data Preferences</h3>
                  <p className="text-[11px] text-black/50">DPDP Act, 2023 Consent Management</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowPreferences(false)}
                className="h-8 w-8 rounded-full hover:bg-black/5 flex items-center justify-center transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1 text-xs">
              {/* Essential */}
              <div className="p-4 rounded-2xl border border-black/10 bg-black/[0.02]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Lock className="h-4 w-4 text-black" />
                    <p className="font-bold text-black">Strictly Necessary Cookies</p>
                  </div>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-black text-white">
                    ALWAYS ACTIVE
                  </span>
                </div>
                <p className="mt-2 text-black/65 leading-relaxed">
                  Required for essential website operations, network security, authentication, and form protection. Cannot be disabled.
                </p>
              </div>

              {/* Analytics */}
              <div className="p-4 rounded-2xl border border-black/10 bg-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-bold text-black">Performance &amp; Analytics</p>
                    <p className="text-[11px] text-black/50">Anonymous traffic &amp; telemetry</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) =>
                        setPreferences({ ...preferences, analytics: e.target.checked })
                      }
                      className="sr-only peer"
                    />
                    <div className="w-10 h-6 bg-black/20 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-black/20 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
                  </label>
                </div>
                <p className="mt-2 text-black/65 leading-relaxed">
                  Helps us understand how visitors interact with the BUGZ X platform to improve speed, user experience, and page loading.
                </p>
              </div>

              {/* Marketing */}
              <div className="p-4 rounded-2xl border border-black/10 bg-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-bold text-black">Marketing &amp; Personalization</p>
                    <p className="text-[11px] text-black/50">Relevant service recommendations</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={(e) =>
                        setPreferences({ ...preferences, marketing: e.target.checked })
                      }
                      className="sr-only peer"
                    />
                    <div className="w-10 h-6 bg-black/20 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-black/20 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
                  </label>
                </div>
                <p className="mt-2 text-black/65 leading-relaxed">
                  Enables tailored content delivery and marketing campaigns based on your industry interests.
                </p>
              </div>
            </div>

            <div className="pt-3 border-t border-black/10 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={handleRejectNonEssential}
                className="text-xs text-black/60 hover:text-black font-semibold underline"
              >
                Reject All Non-Essential
              </button>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleSavePreferences}
                  className="px-4 py-2 rounded-full bg-black text-white text-xs font-bold tracking-wider hover:bg-black/85 transition-colors"
                >
                  SAVE PREFERENCES
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
